// src/modules/app-views/account/pages/Subscription/modals/CertificateUploadModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import uploadStep from './Step1';
import summaryStep from './Step2';

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Convert base64 string to Blob
 * @param {string} base64 - Base64 encoded string
 * @param {string} mimeType - MIME type of the file
 * @returns {Blob}
 */
const base64ToBlob = (base64, mimeType = 'application/x-pkcs12') => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

/**
 * Clean holder name by removing CNPJ/CPF suffix
 * Brazilian certificates format: "COMPANY NAME:CNPJ" or "PERSON NAME:CPF"
 * @param {string} holderName - Raw holder name from certificate
 * @returns {string} Cleaned holder name without document suffix
 */
const cleanHolderName = holderName => {
  if (!holderName || !holderName.includes(':')) return holderName;

  const parts = holderName.split(':');
  const lastPart = parts[parts.length - 1].trim();
  const cleanDoc = lastPart.replace(/\D/g, '');

  // Check if last part is a CNPJ (14 digits) or CPF (11 digits)
  if (cleanDoc.length === 14 || cleanDoc.length === 11) {
    return parts.slice(0, -1).join(':').trim();
  }

  return holderName;
};

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CertificateUploadModal = ({
  show,
  setShow,
  afterClose,
  onSuccess,
  entityId,
  companyCnpj,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form }) => {
    // Show loading message
    const loadingMsg = await appService.message({
      flag: 'loading',
      text: 'certificate_uploading',
      duration: 0, // Keep visible until we update it
    });

    try {
      const certificateInfo = form.getFieldValue('certificateInfo');
      const certificateFile = form.getFieldValue('certificateFile');
      const certificatePassword = form.getFieldValue('certificatePassword');

      if (!certificateInfo || !certificateFile) {
        throw new Error('Certificate data not found');
      }

      if (!entityId) {
        throw new Error('Entity ID not found');
      }

      // Convert base64 to Blob and create File object
      const blob = base64ToBlob(certificateFile.base64);
      const file = new File([blob], certificateFile.name, { type: 'application/x-pkcs12' });

      // Build FormData for upload
      const formData = new FormData();
      formData.append('file', file, certificateFile.name);
      formData.append('password', certificatePassword);
      formData.append('entityId', entityId);

      // Upload to backend
      const [response] = await axiosService.post({
        url: API.FISCAL.CERTIFICATE_UPLOAD,
        data: formData,
        returnAsObject: true,
      });

      if (!response || !response.success) {
        throw new Error('Upload failed');
      }

      // Build certificate data from response
      const certificateData = {
        holderName: cleanHolderName(response.certificate?.holderName),
        cnpj: response.certificate?.cnpj,
        cpf: response.certificate?.cpf,
        document: response.certificate?.document,
        documentType: response.certificate?.documentType,
        serialNumber: response.certificate?.serialNumber,
        issuer: response.certificate?.issuer,
        validFrom: response.certificate?.validFrom,
        expiresAt: response.certificate?.expiresAt,
        status: response.certificate?.status,
        daysUntilExpiry: response.certificate?.daysUntilExpiry,
        uploadedAt: new Date().toISOString(),
        fileName: certificateFile.name,
        fileSize: certificateFile.size,
      };

      // Extract state registration data from response (queried via CCC/SEFAZ)
      const stateRegistrationData = response.stateRegistration?.success
        ? {
            stateRegistration: response.stateRegistration.stateRegistration || null,
            stateRegistrationFormatted:
              response.stateRegistration.stateRegistrationFormatted || null,
            registrationStatus: response.stateRegistration.registrationStatus || null,
            isActive: response.stateRegistration.isActive ?? null,
            taxRegime: response.stateRegistration.taxRegime || null,
          }
        : null;

      // Call onSuccess callback with certificate and state registration data
      if (onSuccess) {
        onSuccess({ certificateData, stateRegistrationData });
      }

      // Update loading message to success
      loadingMsg.update({
        flag: 's',
        text: 'certificate_upload_success',
        duration: 2,
      });

      return true;
    } catch (error) {
      console.error('[CertificateUploadModal] handleFinish error:', error);

      // Update loading message to error
      loadingMsg.update({
        flag: 'e',
        text: 'certificate_upload_error',
        duration: 3,
      });

      throw error;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [
      // Step 1: Upload certificate file + password
      uploadStep(),

      // Step 2: Summary and confirm
      summaryStep(),
    ],
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<SafetyCertificateOutlined />}
      title="upload_digital_certificate"
      steps={steps}
      onFinish={handleFinish}
      initialValues={{ companyCnpj }}
      afterClose={afterClose}
      width={560}
      maskClosable={false}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CertificateUploadModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
